// DESCRIPTION: Event types settings tab component for managing default event types
// and custom templates in the Nemtly Booking admin settings.

import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import {
    TextControl,
    ToggleControl,
    TextareaControl,
    Button,
    Card,
    CardBody,
    CardHeader,
    Modal,
} from '@wordpress/components';
import apiFetch from '@wordpress/api-fetch';

const DEFAULT_TEMPLATE_FORM = {
    label: '',
    description: '',
    icon: '⚙️',
    duration: 30,
    max_attendees: 1,
    buffer_before: 0,
    buffer_after: 15,
    booking_cutoff: 2,
    allow_waitlist: false,
};

/**
 * Event Types Settings Component
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {Object}   props.eventTypes  Map of event type key → config.
 * @param {Function} props.onRefresh   Callback to reload data from API.
 * @param {Function} props.addNotice   Callback to show a toast notice.
 *
 * @return {JSX.Element}
 */
function EventTypesSettings({ eventTypes, onRefresh, addNotice }) {
    const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [templateForm, setTemplateForm] = useState({ ...DEFAULT_TEMPLATE_FORM });

    const createTemplate = async () => {
        setIsCreatingTemplate(true);
        try {
            const response = await apiFetch({
                path: '/nemtly-booking/v1/settings/templates',
                method: 'POST',
                data: templateForm,
            });

            if (response && response.success) {
                addNotice(__('Template created successfully', 'nemtly-booking'), 'success');
                setTemplateForm({ ...DEFAULT_TEMPLATE_FORM });
                setShowCreateForm(false);
                onRefresh();
            } else {
                addNotice(__('Failed to create template', 'nemtly-booking'), 'error');
            }
        } catch (error) {
            addNotice(__('Failed to create template', 'nemtly-booking'), 'error');
        } finally {
            setIsCreatingTemplate(false);
        }
    };

    /**
     * Delete a custom template after modal confirmation
     *
     * @since PBOOKING_SINCE
     *
     * @param {string} templateId The template ID to delete.
     *
     * @return {void}
     */
    const deleteTemplate = async () => {
        setIsDeleting(true);
        try {
            const response = await apiFetch({
                path: `/nemtly-booking/v1/settings/templates/${deleteTarget.id}`,
                method: 'DELETE',
            });

            if (response && response.success) {
                addNotice(__('Template deleted successfully', 'nemtly-booking'), 'success');
                onRefresh();
            } else {
                addNotice(__('Failed to delete template', 'nemtly-booking'), 'error');
            }
        } catch (error) {
            addNotice(__('Failed to delete template', 'nemtly-booking'), 'error');
        } finally {
            setIsDeleting(false);
            setDeleteTarget(null);
        }
    };

    /**
     * Shared card layout for rendering an event type or template.
     *
     * @since PBOOKING_SINCE
     *
     * @param {string}      key      Unique key for the event type.
     * @param {Object}      type     Event type / template config object.
     * @param {JSX.Element} actions  Optional action buttons (e.g. delete).
     *
     * @return {JSX.Element}
     */
    const renderEventTypeCard = (key, type, actions = null) => (
        <div key={key} className={`nb-bg-white nb-border nb-border-slate-200 nb-rounded-lg nb-p-6 nb-shadow-sm nb-transition-shadow ${actions ? 'hover:nb-shadow-md' : ''}`}>
            <div className="nb-flex nb-items-center nb-justify-between nb-mb-4">
                <div className="nb-flex nb-items-center">
                    <span className="nb-text-2xl nb-mr-3">{type.icon}</span>
                    <h4 className="nb-text-lg nb-font-semibold nb-text-slate-900">{type.label}</h4>
                </div>
                {actions}
            </div>
            <p className="nb-text-slate-600 nb-mb-4">{type.description}</p>
            <div className="nb-space-y-2 nb-text-sm nb-text-slate-500">
                <div className="nb-flex nb-justify-between">
                    <span>{__('Duration:', 'nemtly-booking')}</span>
                    <span className="nb-font-medium">{type.duration} {__('minutes', 'nemtly-booking')}</span>
                </div>
                <div className="nb-flex nb-justify-between">
                    <span>{__('Max Attendees:', 'nemtly-booking')}</span>
                    <span className="nb-font-medium">{type.max_attendees === 0 ? __('Unlimited', 'nemtly-booking') : type.max_attendees}</span>
                </div>
                <div className="nb-flex nb-justify-between">
                    <span>{__('Buffer Before:', 'nemtly-booking')}</span>
                    <span className="nb-font-medium">{type.buffer_before ?? 0} {__('minutes', 'nemtly-booking')}</span>
                </div>
                <div className="nb-flex nb-justify-between">
                    <span>{__('Buffer After:', 'nemtly-booking')}</span>
                    <span className="nb-font-medium">{type.buffer_after ?? 0} {__('minutes', 'nemtly-booking')}</span>
                </div>
                <div className="nb-flex nb-justify-between">
                    <span>{__('Booking Cutoff:', 'nemtly-booking')}</span>
                    <span className="nb-font-medium">{type.booking_cutoff} {__('hours', 'nemtly-booking')}</span>
                </div>
            </div>
        </div>
    );

    return (
        <div className="nb-space-y-6">
            {/* Delete Confirmation Modal */}
            {deleteTarget && (
                <Modal
                    title={__('Delete Template', 'nemtly-booking')}
                    onRequestClose={() => !isDeleting && setDeleteTarget(null)}
                    className="nb-delete-confirm-modal"
                >
                    <p className="nb-text-slate-700 nb-mb-2">
                        {__('Are you sure you want to delete the following template?', 'nemtly-booking')}
                    </p>
                    <p className="nb-text-slate-900 nb-font-semibold nb-mb-6">
                        {deleteTarget.label}
                    </p>
                    <p className="nb-text-sm nb-text-slate-500 nb-mb-6">
                        {__('This action cannot be undone.', 'nemtly-booking')}
                    </p>
                    <div className="nb-flex nb-justify-end nb-gap-3">
                        <Button
                            variant="tertiary"
                            onClick={() => setDeleteTarget(null)}
                            disabled={isDeleting}
                        >
                            {__('Cancel', 'nemtly-booking')}
                        </Button>
                        <Button
                            className="nb-settings-button-error nb-text-white"
                            onClick={deleteTemplate}
                            isBusy={isDeleting}
                            disabled={isDeleting}
                        >
                            {isDeleting ? __('Deleting...', 'nemtly-booking') : __('Delete', 'nemtly-booking')}
                        </Button>
                    </div>
                </Modal>
            )}

            {/* Default Event Types */}
            <Card className="nb-border nb-border-slate-200 nb-shadow-sm">
                <CardHeader className="nb-bg-slate-50">
                    <div className="nb-flex nb-items-center nb-gap-3">
                        <div className="nb-w-8 nb-h-8 nb-bg-slate-600 nb-rounded-lg nb-flex nb-items-center nb-justify-center">
                            <svg className="nb-w-4 nb-h-4 nb-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="nb-text-lg nb-font-semibold nb-text-slate-900">{__('Default Event Types', 'nemtly-booking')}</h3>
                            <p className="nb-text-sm nb-text-slate-600">{__('Pre-configured event types for quick setup', 'nemtly-booking')}</p>
                        </div>
                    </div>
                </CardHeader>
                <CardBody>
                    <div className="nb-grid nb-grid-cols-1 md:nb-grid-cols-2 lg:nb-grid-cols-3 nb-gap-6">
                        {Object.entries(eventTypes).filter(([key]) => !key.startsWith('template_')).map(([key, type]) => (
                            renderEventTypeCard(key, type)
                        ))}
                    </div>
                </CardBody>
            </Card>

            {/* Custom Templates */}
            <Card className="nb-border nb-border-slate-200 nb-shadow-sm">
                <CardHeader className="nb-bg-slate-50">
                    <div className="nb-flex nb-items-center nb-gap-3">
                        <div className="nb-w-8 nb-h-8 nb-bg-slate-600 nb-rounded-lg nb-flex nb-items-center nb-justify-center">
                            <svg className="nb-w-4 nb-h-4 nb-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="nb-text-lg nb-font-semibold nb-text-slate-900">{__('Custom Templates', 'nemtly-booking')}</h3>
                            <p className="nb-text-sm nb-text-slate-600">{__('Create and manage custom event type templates', 'nemtly-booking')}</p>
                        </div>
                    </div>
                </CardHeader>
                <CardBody className="nb-space-y-6">
                    {Object.entries(eventTypes).filter(([key]) => key.startsWith('template_')).length === 0 ? (
                        <div className="nb-text-center nb-py-12 nb-text-slate-500">
                            <svg className="nb-w-12 nb-h-12 nb-mx-auto nb-mb-4 nb-text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="nb-text-lg nb-font-medium">{__('No custom templates created yet.', 'nemtly-booking')}</p>
                            <p className="nb-text-sm">{__('Create your first custom template below.', 'nemtly-booking')}</p>
                        </div>
                    ) : (
                        <div className="nb-grid nb-grid-cols-1 md:nb-grid-cols-2 lg:nb-grid-cols-3 nb-gap-6">
                            {Object.entries(eventTypes).filter(([key]) => key.startsWith('template_')).map(([key, template]) => (
                                renderEventTypeCard(key, template, (
                                    <Button
                                        isSmall
                                        onClick={() => setDeleteTarget({ id: template.template_id, label: template.label })}
                                        className="nb-settings-button-error nb-text-xs"
                                    >
                                        {__('Delete', 'nemtly-booking')}
                                    </Button>
                                ))
                            ))}
                        </div>
                    )}

                    {/* Create Template Toggle + Form */}
                    {!showCreateForm ? (
                        <div className="nb-flex nb-justify-center">
                            <Button
                                variant="secondary"
                                onClick={() => setShowCreateForm(true)}
                                className="nb-flex nb-items-center nb-gap-2"
                            >
                                <svg className="nb-w-4 nb-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                {__('Create Custom Template', 'nemtly-booking')}
                            </Button>
                        </div>
                    ) : (
                        <Card className="nb-bg-slate-50 nb-border-slate-200">
                            <CardHeader>
                                <div className="nb-flex nb-items-center nb-justify-between">
                                    <div className="nb-flex nb-items-center nb-gap-3">
                                        <div className="nb-w-6 nb-h-6 nb-bg-slate-600 nb-rounded-lg nb-flex nb-items-center nb-justify-center">
                                            <svg className="nb-w-3 nb-h-3 nb-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                            </svg>
                                        </div>
                                        <h4 className="nb-text-base nb-font-semibold nb-text-slate-900">{__('Create Custom Template', 'nemtly-booking')}</h4>
                                    </div>
                                    <Button
                                        variant="tertiary"
                                        isSmall
                                        onClick={() => {
                                            setShowCreateForm(false);
                                            setTemplateForm(DEFAULT_TEMPLATE_FORM);
                                        }}
                                    >
                                        {__('Cancel', 'nemtly-booking')}
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <div className="nb-space-y-6">
                                    <div className="nb-grid nb-grid-cols-1 md:nb-grid-cols-2 nb-gap-6">
                                        <TextControl
                                            label={__('Template Name', 'nemtly-booking')}
                                            value={templateForm.label}
                                            onChange={(value) => setTemplateForm(prev => ({ ...prev, label: value }))}
                                            required
                                            help={!templateForm.label.trim() ? __('Required — give your template a name.', 'nemtly-booking') : undefined}
                                            className={!templateForm.label.trim() ? 'nb-template-name-empty' : ''}
                                        />
                                        <TextControl
                                            label={__('Icon', 'nemtly-booking')}
                                            value={templateForm.icon}
                                            onChange={(value) => setTemplateForm(prev => ({ ...prev, icon: value }))}
                                            help={__('Emoji or icon for the template', 'nemtly-booking')}
                                        />
                                    </div>

                                    <TextareaControl
                                        label={__('Description', 'nemtly-booking')}
                                        value={templateForm.description}
                                        onChange={(value) => setTemplateForm(prev => ({ ...prev, description: value }))}
                                        rows={2}
                                    />

                                    <div className="nb-grid nb-grid-cols-1 md:nb-grid-cols-2 nb-gap-6">
                                        <TextControl
                                            label={__('Duration (minutes)', 'nemtly-booking')}
                                            value={templateForm.duration}
                                            onChange={(value) => setTemplateForm(prev => ({ ...prev, duration: parseInt(value) || 30 }))}
                                            type="number"
                                            min="5"
                                            max="480"
                                        />
                                        <TextControl
                                            label={__('Max Attendees', 'nemtly-booking')}
                                            value={templateForm.max_attendees}
                                            onChange={(value) => setTemplateForm(prev => ({ ...prev, max_attendees: parseInt(value) || 1 }))}
                                            type="number"
                                            min="0"
                                            help={__('0 = unlimited', 'nemtly-booking')}
                                        />
                                    </div>

                                    <div className="nb-grid nb-grid-cols-1 md:nb-grid-cols-3 nb-gap-6">
                                        <TextControl
                                            label={__('Buffer Before (minutes)', 'nemtly-booking')}
                                            value={templateForm.buffer_before}
                                            onChange={(value) => setTemplateForm(prev => ({ ...prev, buffer_before: parseInt(value) || 0 }))}
                                            type="number"
                                            min="0"
                                            max="120"
                                        />
                                        <TextControl
                                            label={__('Buffer After (minutes)', 'nemtly-booking')}
                                            value={templateForm.buffer_after}
                                            onChange={(value) => setTemplateForm(prev => ({ ...prev, buffer_after: parseInt(value) || 0 }))}
                                            type="number"
                                            min="0"
                                            max="120"
                                        />
                                        <TextControl
                                            label={__('Booking Cutoff (hours)', 'nemtly-booking')}
                                            value={templateForm.booking_cutoff}
                                            onChange={(value) => setTemplateForm(prev => ({ ...prev, booking_cutoff: parseInt(value) || 2 }))}
                                            type="number"
                                            min="0"
                                            max="168"
                                        />
                                    </div>

                                    <ToggleControl
                                        label={__('Allow Waitlist', 'nemtly-booking')}
                                        checked={templateForm.allow_waitlist}
                                        onChange={(value) => setTemplateForm(prev => ({ ...prev, allow_waitlist: value }))}
                                    />

                                    <div className="nb-flex nb-justify-end nb-gap-3">
                                        <Button
                                            variant="tertiary"
                                            onClick={() => {
                                                setShowCreateForm(false);
                                                setTemplateForm(DEFAULT_TEMPLATE_FORM);
                                            }}
                                            disabled={isCreatingTemplate}
                                        >
                                            {__('Cancel', 'nemtly-booking')}
                                        </Button>
                                        <Button
                                            onClick={createTemplate}
                                            isBusy={isCreatingTemplate}
                                            disabled={!templateForm.label.trim() || isCreatingTemplate}
                                            className="nb-settings-button-primary nb-px-6 nb-py-2.5 nb-text-white"
                                        >
                                            {isCreatingTemplate ? __('Creating...', 'nemtly-booking') : __('Create Template', 'nemtly-booking')}
                                        </Button>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    )}
                </CardBody>
            </Card>
        </div>
    );
}

export default EventTypesSettings;
